import { memo, useEffect, useState } from 'react';

import { SearchOutlined } from '@ant-design/icons';
import { Button, Card, Checkbox, Collapse, Input, Spin, Tag } from 'antd';

import { FiltersData } from '../../../../../api/filters/reqres';
import { debounce, propsAreEqual } from '../../../../../util';
import './ProductsFilters.less';

const { Panel } = Collapse;

export interface FiltersType {
  brands?: FiltersData[];
  tags?: FiltersData[];
  format?: FiltersData[];
  market?: FiltersData[];
  quality?: FiltersData[];
  private?: FiltersData[];
  flex?: FiltersData[];
  drc?: FiltersData[];
  parentco?: FiltersData[];
  approved?: FiltersData[];
}

export type SelectedFilters = {
  [filter in keyof FiltersType]: string[];
};

interface ProductsFiltersProps {
  filters: FiltersType;
  loading: boolean;
  onFiltersSelected: (selectedFilters: SelectedFilters) => void;
  selectedFilter?: SelectedFilters;
}

const ProductsFilters: React.FC<ProductsFiltersProps> = ({
  selectedFilter,
  filters,
  loading,
  onFiltersSelected,
}) => {
  const [filterQueries, setFilterQueries] = useState<{
    [filter in keyof FiltersType]: string;
  }>({});
  const [filteredFilters, setFilteredFilters] = useState<FiltersType>(filters);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(
    selectedFilter ?? {}
  );
  const [isFiltering, setIsFiltering] = useState<boolean>(false);

  // Initialize the approved filter if it doesn't exist
  useEffect(() => {
    if (!filters.approved) {
      const approvedFilters: FiltersData[] = [
        { key: 'approved', name: 'Approved', count: 0 },
        { key: 'unapproved', name: 'Not Approved', count: 0 },
        { key: 'both', name: 'Both', count: 0 },
      ];

      setFilteredFilters((prev) => ({
        ...prev,
        approved: approvedFilters,
      }));
    }
  }, [filters]);

  const queryChangeHandler = (filter: keyof FiltersType, query: string) => {
    setIsFiltering(true);
    setFilterQueries({
      ...filterQueries,
      [filter]: query,
    });
  };

  const handleQueryChange = debounce(queryChangeHandler, 300);

  useEffect(() => {
    const filtered = { ...filters };
    Object.keys(filterQueries).forEach((filterGroup) => {
      const filterKey = filterGroup as keyof FiltersType;
      const query = filterQueries[filterKey] || '';
      if (!!query) {
        const filteredGroup = filters[filterKey]?.filter((filter) => {
          const pattern = new RegExp(query.toLowerCase());
          return pattern.test(filter.name.toLowerCase());
        });

        filtered[filterKey] = filteredGroup;
      }
    });

    if (!filtered.approved && filteredFilters.approved) {
      filtered.approved = filteredFilters.approved;
    }

    setFilteredFilters(filtered);
    setIsFiltering(false);
  }, [filterQueries, filters, filteredFilters.approved]);

  const removeSelectedFilter = (filterGroup: string, filter: FiltersData) => {
    const filterKey = filterGroup as keyof FiltersType;

    const selected = {
      ...selectedFilters,
      [filterGroup]: selectedFilters[filterKey]?.filter(
        (key) => key !== filter.key
      ),
    };

    setSelectedFilters(selected);
    onFiltersSelected(selected);
  };

  const handleSelectFilters = (filterGroup: string, filter: FiltersData) => {
    const filterKey = filterGroup as keyof FiltersType;
    let selected = { ...selectedFilters };

    if (filterGroup === 'approved') {
      selected = {
        ...selectedFilters,
        [filterKey]: [filter.key],
      };
    } else if (selectedFilters[filterKey]?.includes(filter.key)) {
      removeSelectedFilter(filterGroup, filter);
      return;
    } else {
      selected = {
        ...selectedFilters,
        [filterKey]: selectedFilters[filterKey]
          ? [...(selectedFilters[filterKey] ?? []), filter.key]
          : [filter.key],
      };
    }

    setSelectedFilters(selected);
    onFiltersSelected(selected);
  };

  useEffect(() => {
    if (!selectedFilter) return;
    onFiltersSelected(selectedFilter);
  }, [onFiltersSelected, selectedFilter]);

  const hasFilters = () => {
    return Object.keys(selectedFilters).some((filterGroup) => {
      const filterKey = filterGroup as keyof FiltersType;

      return (selectedFilters[filterKey] || []).length > 0;
    });
  };

  if (loading) {
    return (
      <div className="filters-wrapper">
        <Card className="filters-spinner-wrapper">
          <Spin tip="Loading filters..." />
        </Card>
      </div>
    );
  }

  return (
    <div className="filters-wrapper">
      <div className="filters-header">
        <span className="filters-title">Filter</span>
        {hasFilters() ? (
          <div className="selected-filters-wrapper">
            <div className="selected-filters-header">
              <span className="selected-filters-title">SELECTED FILTERS</span>
              <Button
                type="link"
                className="clear-btn"
                onClick={() => {
                  setSelectedFilters({});
                  onFiltersSelected({});
                }}
              >
                Clear
              </Button>
            </div>
            <div className="selected-filters">
              {Object.keys(selectedFilters).flatMap((filterGroup) => {
                const filterKey = filterGroup as keyof FiltersType;

                return selectedFilters[filterKey]?.map((filter) => {
                  const filterData = (
                    filterKey === 'approved' && filteredFilters.approved
                      ? filteredFilters.approved
                      : (filters[filterKey] as FiltersData[])
                  )?.find((data) => data.key === filter);

                  if (!filterData) return null;
                  return (
                    <Tag
                      className="filter-tag"
                      key={filter}
                      closable
                      style={{ color: '#003D4C', borderColor: '#003D4C' }}
                      onClose={() =>
                        removeSelectedFilter(
                          filterGroup,
                          filterData as FiltersData
                        )
                      }
                    >
                      {filterData?.name}
                    </Tag>
                  );
                });
              })}
            </div>
          </div>
        ) : null}
      </div>
      <Collapse
        defaultActiveKey={['1']}
        onChange={() => {}}
        expandIconPosition="right"
      >
        {filteredFilters.approved && (
          <Panel
            header={<span className="panel-header-title">APPROVAL STATUS</span>}
            key="0"
          >
            <div className="panel-filters-wrapper">
              {filteredFilters.approved?.map((filter) => (
                <Checkbox
                  key={filter.key}
                  checked={selectedFilters.approved?.includes(filter.key)}
                  onChange={() => handleSelectFilters('approved', filter)}
                >
                  {filter.name}
                </Checkbox>
              ))}
            </div>
          </Panel>
        )}

        {Object.keys(filteredFilters).map((filterGroup, index) => {
          const filterKey = filterGroup as keyof FiltersType;

          if (filterKey === 'approved') return null;

          return (
            <Panel
              header={
                <span className="panel-header-title">
                  {filterGroup === 'parentco'
                    ? 'PARENT COMPANY'
                    : filterGroup.toUpperCase()}
                </span>
              }
              key={`${index + 1}`}
            >
              <div className="panel-search-wrapper">
                <Input
                  prefix={<SearchOutlined />}
                  onChange={(e) => handleQueryChange(filterKey, e.target.value)}
                />
              </div>
              {isFiltering ? (
                <div className="group-spinner-wrapper">
                  <Spin />
                </div>
              ) : (
                <div className="panel-filters-wrapper">
                  {filteredFilters[filterKey]?.map((filter) => (
                    <Checkbox
                      key={filter.key}
                      checked={selectedFilters[filterKey]?.includes(filter.key)}
                      onChange={() => handleSelectFilters(filterGroup, filter)}
                    >
                      {filter.name}
                    </Checkbox>
                  ))}
                </div>
              )}
            </Panel>
          );
        })}
      </Collapse>
    </div>
  );
};

export default memo(ProductsFilters, propsAreEqual);
